import { useEffect, useState, useMemo } from "react";
import api, { formatIDR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileXls, TrendUp, TrendDown, Wallet, ArrowDown, ArrowUp } from "@phosphor-icons/react";

const now = new Date();
const MONTHS_ID = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];

export default function PnL() {
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const monthOptions = useMemo(() => Array.from({ length: 24 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return { key, label: `${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}` };
  }), []);

  useEffect(() => {
    setBusy(true);
    api.get(`/reports/pnl?month=${month}`).then((r) => setData(r.data)).finally(() => setBusy(false));
  }, [month]);

  const download = async () => {
    try {
      const r = await api.get(`/reports/pnl.xlsx?month=${month}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `laba-rugi-${month}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel diunduh");
    } catch { toast.error("Gagal mengunduh"); }
  };

  const d = data || {};
  const netPositive = (d.net_profit || 0) >= 0;

  return (
    <div>
      <PageHeader title="Laporan Laba / Rugi" subtitle="Konsolidasi laba bersih bulanan — pemasukan dikurangi HPP, biaya, komisi & diskon"
        actions={
          <Button onClick={download} data-testid="pnl-download" className="rounded-none bg-[#10B981] hover:bg-[#0d966b] text-white uppercase tracking-wider text-xs h-10">
            <FileXls size={14} className="mr-2" weight="bold" /> Export Excel
          </Button>
        }
      />
      <div className="p-4 md:p-6 lg:p-10 max-w-5xl">
        <div className="mb-6 max-w-xs">
          <Label className="text-[10px] uppercase tracking-widest text-gray-500">Bulan</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="pnl-month" className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {busy && <div className="text-center text-gray-500 py-10">Memuat...</div>}
        {!busy && data && (
          <div className="border border-gray-200 bg-white">
            {/* Hero Net Profit */}
            <div className="p-8 border-b border-gray-200 flex items-center justify-between" style={{ background: netPositive ? "#F0FDF4" : "#FEF2F2" }}>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">Laba Bersih {MONTHS_ID[parseInt(month.split("-")[1]) - 1]} {month.split("-")[0]}</div>
                <div className="font-mono-num font-bold text-4xl mt-2" style={{ color: netPositive ? "#10B981" : "#E63946" }} data-testid="pnl-net">
                  {formatIDR(d.net_profit)}
                </div>
                <div className="text-sm text-gray-600 mt-1 font-mono-num">
                  Margin: <span className="font-semibold" data-testid="pnl-margin">{(d.margin_pct || 0).toFixed(1)}%</span> dari penjualan
                </div>
              </div>
              {netPositive ? <TrendUp size={64} weight="bold" className="text-emerald-600 opacity-40" /> : <TrendDown size={64} weight="bold" className="text-rose-600 opacity-40" />}
            </div>

            {/* Breakdown */}
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-gray-100 bg-emerald-50/40">
                  <td className="p-4 uppercase tracking-widest text-[10px] font-bold text-emerald-800" colSpan="2">
                    <ArrowDown size={12} className="inline mr-1" weight="bold" /> Pemasukan
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="p-4 pl-8">Penjualan Mobil <span className="text-xs text-gray-500 font-mono-num">({d.sold_count || 0} unit)</span></td>
                  <td className="p-4 text-right font-mono-num font-semibold text-emerald-700" data-testid="pnl-revenue">{formatIDR(d.revenue)}</td>
                </tr>

                <tr className="border-b border-gray-100 bg-rose-50/40">
                  <td className="p-4 uppercase tracking-widest text-[10px] font-bold text-rose-800" colSpan="2">
                    <ArrowUp size={12} className="inline mr-1" weight="bold" /> Pengeluaran
                  </td>
                </tr>
                {[
                  ["HPP (harga beli mobil terjual)", d.hpp, "pnl-hpp"],
                  ["Biaya Mobil (perbaikan/servis/sparepart)", d.biaya_mobil, "pnl-biaya"],
                  ["Komisi Makelar", d.komisi, "pnl-komisi"],
                  ["Diskon Nego Cicilan", d.diskon_nego, "pnl-diskon-nego"],
                  ["Diskon Bunga Pelunasan Cepat", d.interest_writeoff, "pnl-writeoff"],
                ].map(([label, val, tid]) => (
                  <tr key={tid} className="border-b border-gray-100">
                    <td className="p-4 pl-8">{label}</td>
                    <td className="p-4 text-right font-mono-num font-semibold text-rose-700" data-testid={tid}>{formatIDR(val)}</td>
                  </tr>
                ))}
                <tr className="border-b-2 border-gray-900 bg-gray-50">
                  <td className="p-4 pl-8 font-bold uppercase text-xs tracking-wider">Total Pengeluaran</td>
                  <td className="p-4 text-right font-mono-num font-bold text-rose-800" data-testid="pnl-total-cost">{formatIDR(d.total_cost)}</td>
                </tr>

                <tr className="bg-yellow-50">
                  <td className="p-4 uppercase tracking-widest text-sm font-bold">
                    <Wallet size={14} className="inline mr-2" weight="bold" /> Laba Bersih
                  </td>
                  <td className="p-4 text-right font-mono-num font-bold text-lg" style={{ color: netPositive ? "#10B981" : "#E63946" }}>
                    {formatIDR(d.net_profit)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 text-xs text-gray-500 font-mono-num">
          Rumus: Laba Bersih = Penjualan − HPP − Biaya Mobil − Komisi Makelar − Diskon Nego − Diskon Bunga Pelunasan
        </div>
      </div>
    </div>
  );
}
